import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Avatar,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps,
} from '@fluentui/react-components';
import * as Functions from './SponsorSelectorFunctions';
import { Sponsor } from '../../../../models/Entities/Sponsor/Sponsor';

export interface ISponsorSelectorProps {
  selectionLimit: number;
  onChangeSelection?: (selected: Sponsor | Sponsor[]) => void;
}

const SponsorSelectorInner = (props: Functions.Props) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');
  const [hasAutoInit, setHasAutoInit] = useState(false);

  useEffect(() => {
    props.GetAllSponsors(1, 1000, undefined);
  }, []);

  // Auto-initialize selection from initialSponsorId when sponsors load
  useEffect(() => {
    if (!hasAutoInit && props.initialSponsorId && props.sponsors.length > 0) {
      const idStr = String(props.initialSponsorId);
      setSelectedOptions([idStr]);
      setHasAutoInit(true);
      const sponsor = props.sponsors.find((s) => String(s.id) === idStr);
      if (sponsor && props.onChangeSelection) {
        props.onChangeSelection(sponsor);
      }
    }
  }, [props.sponsors, props.initialSponsorId, hasAutoInit]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (_, data) => {
    if (data.selectedOptions.length === 0) {
      setSelectedOptions([]);
      setFilterText('');
      props.onChangeSelection && props.onChangeSelection([]);
      return;
    }

    const newSelected =
      props.selectionLimit === 1 ? [data.value] : data.selectedOptions;

    setSelectedOptions(newSelected);
    setFilterText('');

    const selectedSponsors = props.sponsors.filter((s) =>
      newSelected.includes(String(s.id))
    );

    if (!props.onChangeSelection) return;

    if (props.selectionLimit === 1) {
      if (selectedSponsors.length > 0) props.onChangeSelection(selectedSponsors[0]);
    } else {
      props.onChangeSelection(selectedSponsors);
    }
  };

  const tagPickerOptions = props.sponsors
    .filter((s) => !selectedOptions.includes(String(s.id)))
    .filter(
      (s) =>
        filterText === '' ||
        s.person.businessName.toLowerCase().includes(filterText.toLowerCase())
    );

  const getDisplayName = (id: string) =>
    props.sponsors.find((s) => String(s.id) === id)?.person.businessName ?? id;

  return (
    <TagPicker onOptionSelect={onOptionSelect} selectedOptions={selectedOptions}>
      <TagPickerControl>
        <TagPickerGroup aria-label="Sponsors seleccionados">
          {selectedOptions.map((id) => (
            <Tag
              key={id}
              media={
                <Avatar
                  shape="circular"
                  aria-hidden
                  name={getDisplayName(id)}
                  color="colorful"
                />
              }
              value={id}
            >
              {getDisplayName(id)}
            </Tag>
          ))}
        </TagPickerGroup>
        <TagPickerInput
          aria-label="Buscar sponsor"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        {props.isLoading && (
          <Spinner size="extra-tiny" style={{ marginLeft: 8 }} />
        )}
      </TagPickerControl>
      <TagPickerList>
        {tagPickerOptions.length > 0 ? (
          tagPickerOptions.map((sponsor) => (
            <TagPickerOption
              key={String(sponsor.id)}
              value={String(sponsor.id)}
              media={
                <Avatar
                  shape="circular"
                  aria-hidden
                  name={sponsor.person.businessName}
                  color="colorful"
                />
              }
            >
              {sponsor.person.businessName}
            </TagPickerOption>
          ))
        ) : null}
      </TagPickerList>
    </TagPicker>
  );
};

export const SponsorSelectorComponent = connect(
  Functions.mapStateToProps,
  Functions.mapDispatchToProps
)(SponsorSelectorInner as any);
